import moment from "moment";
import { Tabs, TabsList, TabsTrigger } from "../../shadcn/Tabs";
import DatePicker from "../../DatePicker";
import SearchFilterLayout from "./SearchFilterLayout";

const DATE_FORMAT = "MM/DD/YYYY";

export const DateSearchType = {
  TODAY: "today",
  LAST_7_DAYS: "last7",
  LAST_30_DAYS: "last30",
  CUSTOM: "custom",
} as const;

export type DateSearchTypeValue = typeof DateSearchType[keyof typeof DateSearchType];

interface SearchDateTypeOption {
  id: string;
  label: string;
  value: string;
  short?: string;
}

interface SmartDateFilterProps {
  fromDate: string;
  toDate: string;
  dateSearchType: string;
  setFromDate: (date: string) => void;
  setToDate: (date: string) => void;
  setDateRange: (from: string, to: string, preset?: string) => void;
  setDateSearchType: (type: string) => void;
  showSearchDateType?: boolean;
  searchDateType?: string;
  setSearchDateType?: (type: string) => void;
  searchDateTypeOptions?: SearchDateTypeOption[];
}

const getPresetDates = (preset: DateSearchTypeValue): { from: string; to: string } => {
  const today = moment().format(DATE_FORMAT);

  switch (preset) {
    case DateSearchType.TODAY:
      return { from: today, to: today };
    case DateSearchType.LAST_7_DAYS:
      return { from: moment().subtract(7, "days").format(DATE_FORMAT), to: today };
    case DateSearchType.LAST_30_DAYS:
      return { from: moment().subtract(30, "days").format(DATE_FORMAT), to: today };
    default:
      return { from: "", to: "" };
  }
};

const SmartDateFilter = ({
  fromDate,
  toDate,
  dateSearchType,
  setFromDate,
  setToDate,
  setDateRange,
  setDateSearchType,
  showSearchDateType = false,
  searchDateType,
  setSearchDateType,
  searchDateTypeOptions = [],
}: SmartDateFilterProps) => {
  const handlePresetChange = (preset: string) => {
    if (preset === DateSearchType.CUSTOM) {
      setDateSearchType(DateSearchType.CUSTOM);
      return;
    }

    const dates = getPresetDates(preset as DateSearchTypeValue);
    setDateRange(dates.from, dates.to, preset);
  };

  return (
    <div className="flex flex-col gap-3 border border-gray-200 rounded-lg p-4">
      {showSearchDateType && setSearchDateType && searchDateTypeOptions.length === 2 && (
        <div className="flex justify-center">
          <Tabs value={searchDateType} onValueChange={setSearchDateType}>
            <TabsList>
              {searchDateTypeOptions.map((option) => (
                <TabsTrigger key={option.id} value={option.value}>
                  {option.short}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}
      <Tabs value={dateSearchType} onValueChange={handlePresetChange}>
        <TabsList className="w-full">
          <TabsTrigger value={DateSearchType.TODAY} className="flex-1 min-w-0">
            Today
          </TabsTrigger>
          <TabsTrigger value={DateSearchType.LAST_7_DAYS} className="flex-1 min-w-0">
            7 days
          </TabsTrigger>
          <TabsTrigger value={DateSearchType.LAST_30_DAYS} className="flex-1 min-w-0">
            30 days
          </TabsTrigger>
          <TabsTrigger value={DateSearchType.CUSTOM} className="flex-1 min-w-0">
            Custom
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {dateSearchType === DateSearchType.CUSTOM && (
        <div className="flex gap-2 flex-grow flex-col sm:flex-row pb-2">
          <div className="h-16 gap-1 flex-1">
            <SearchFilterLayout label="From">
              <DatePicker
                onChange={setFromDate}
                value={fromDate}
                position="bottom-left"
              />
            </SearchFilterLayout>
          </div>
          <div className="h-16 gap-1 flex-1">
            <SearchFilterLayout label="To">
              <DatePicker
                onChange={setToDate}
                value={toDate}
                position="bottom-left"
              />
            </SearchFilterLayout>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartDateFilter;
